import { Spinner, usePageNavigation } from '@shared/components';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsers } from '../../hooks/useUsers';
import { useRoles } from '../../hooks/useRoles';
import { UsersView } from '../display/UsersView';
import { ErrorState } from '../feedback';

const URL_SYNC_DEBOUNCE_MS = 500;

function buildRolesMap(roles: { id: number; name: string }[]): Record<number, string> {
  return roles.reduce<Record<number, string>>((acc, role) => {
    acc[role.id] = role.name;
    return acc;
  }, {});
}

function filterUsers(
  users: { id: number; name: string; email: string; current_role_id: number }[],
  search: string,
  rolesMap: Record<number, string>,
) {
  if (!users.length) {
    return [];
  }

  const searchLower = search.toLowerCase().trim();
  if (!searchLower) {
    return users;
  }

  return users.filter((user) => {
    if (user.name?.toLowerCase().includes(searchLower)) return true;
    if (user.email?.toLowerCase().includes(searchLower)) return true;

    const roleName = rolesMap[user.current_role_id];
    return roleName?.toLowerCase().includes(searchLower) ?? false;
  });
}

export const Main = () => {
  const { t } = useTranslation('usersModule');
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
  });
  const [searchTerm, setSearchTerm] = useState(() => queryParams.search ?? '');
  const deferredSearch = useDeferredValue(searchTerm);
  const urlSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { roles } = useRoles();
  const { users: allUsers, isLoading, isFetching, error, refetch } = useUsers();

  const rolesMap = useMemo(() => buildRolesMap(roles), [roles]);

  const filteredUsers = useMemo(
    () => filterUsers(allUsers, deferredSearch, rolesMap),
    [allUsers, deferredSearch, rolesMap],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  useEffect(() => {
    if (urlSyncTimerRef.current) {
      clearTimeout(urlSyncTimerRef.current);
    }

    urlSyncTimerRef.current = setTimeout(() => {
      const urlSearch = queryParams.search ?? '';
      if (urlSearch !== searchTerm) {
        updateQueryParams({ search: searchTerm });
      }
    }, URL_SYNC_DEBOUNCE_MS);

    return () => {
      if (urlSyncTimerRef.current) {
        clearTimeout(urlSyncTimerRef.current);
      }
    };
  }, [searchTerm, queryParams.search, updateQueryParams]);

  const breadcrumbs = useMemo(() => [{ label: t('pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16" data-testid="users-loading">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-16">
        <ErrorState error={error as Error} onRetry={refetch} className="h-auto" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <UsersView
        users={filteredUsers}
        allUsersCount={allUsers.length}
        rolesMap={rolesMap}
        searchTerm={searchTerm}
        isFetching={isFetching}
        isFiltering={searchTerm !== deferredSearch}
        onSearchChange={handleSearchChange}
        onRefetch={refetch}
      />
    </div>
  );
};
